import React, { useState, useEffect } from "react";
import { Plus, Trash2, Play } from "lucide-react";
import { toast } from "sonner";
import { jwtDecode } from "jwt-decode";
import { TriggerNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { RightDot } from "../../../shared/action-core";
import { NodeMenu } from "../../../shared/trigger-shared/node-menu";
import { DeleteModal } from "../../../shared/trigger-shared/delete-modal";
import useJouneyReq from "../../../hook/bot-req";

// /v1/events/external requires tenant_id in the body (Joseph 06-30:
// HTTP 422 "tenant_id Field required"). The JWT sometimes carries it
// (older tokens do; newer ones don't) so we look there first, then
// fall back to the /auth/get_userdata endpoint which the CRM auth
// service exposes with { payload: { org_id, user_id } }. org_id IS
// tenant_id downstream. Cached in localStorage under the same key
// customize-flow-modal.tsx uses (`linkedin_tenant_id`).
function tenantIdFromToken(token: string): string {
  try {
    const decoded: any = jwtDecode(token);
    return decoded?.tenant_id
      ?? decoded?.tenantId
      ?? decoded?.tenant
      ?? decoded?.org_id
      ?? "";
  } catch {
    return "";
  }
}

async function resolveTenantId(token: string): Promise<string> {
  // Fast path 1: JWT already has it.
  const fromJwt = tenantIdFromToken(token);
  if (fromJwt) return fromJwt;
  // Fast path 2: already cached from a prior fetch.
  const cached = localStorage.getItem(TENANT_ID_LS_KEY);
  if (cached) return cached;
  // Slow path: hit the auth service and cache the result.
  // NOTE: /auth/get_userdata CORS preflight allows ONLY the
  // `authorization` header — sending `Content-Type` would trigger a
  // preflight rejection and the browser surfaces it as "Failed to
  // fetch". This is a GET so we don't need Content-Type anyway.
  try {
    const res = await fetch(`${AUTH_URL}/auth/get_userdata`, {
      mode: "cors",
      credentials: "omit",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) return "";
    const data = await res.json();
    const orgId: string = data?.payload?.org_id
      ?? data?.payload?.tenant_id
      ?? data?.org_id
      ?? data?.tenant_id
      ?? "";
    if (orgId) localStorage.setItem(TENANT_ID_LS_KEY, orgId);
    return orgId;
  } catch (e) {
    // Log so the operator can inspect DevTools even though the toast
    // only shows a summary.
    // eslint-disable-next-line no-console
    console.error("[OnEvent] resolveTenantId failed:", e);
    return "";
  }
}

// ── Test lead form (only shown for appointment-style triggers) ────────
// Joseph 06-30: the form belongs on Appointment Reminder (where the
// operator needs to fire a test event with first_name + phone + date
// + time) but NOT on Feedback Collection (where the trigger is event-
// driven from an external system and there's no per-event date/time
// to configure). Show it only when the trigger event is appointment-
// related.
// Trigger event types that render the appointment-style lead form
// (Phone / First / Last / Date / Time / Timezone) inline. Feedback
// Collection uses a smaller collapsible test dropdown with just a
// phone + first-name input (Joseph 07-01).
const FB_EVENT_TYPES = new Set<string>([
  "Feedback_Collection_Start",
  "On_Ticket_Closed",
  "Ticket_Closed",
]);
const APPT_EVENT_TYPES = new Set<string>([
  "Appointment_Scheduled",
  "Appointment_Reminder",
  "Appointment_Missed",
]);
type TestLead = {
  first_name: string;
  last_name: string;
  phone: string;
  appointment_date: string;  // YYYY-MM-DD, local
  appointment_time: string;  // HH:MM 24h, local
  timezone: string;
};
const EMPTY_LEAD: TestLead = {
  first_name: "", last_name: "", phone: "",
  appointment_date: "", appointment_time: "", timezone: "",
};
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "",                  label: "Use your timezone" },
  { value: "Africa/Lagos",      label: "Lagos (WAT, +01:00)" },
  { value: "Africa/Nairobi",    label: "Nairobi (EAT, +03:00)" },
  { value: "Asia/Kolkata",      label: "India (IST, +05:30)" },
  { value: "Asia/Dubai",        label: "Dubai (GST, +04:00)" },
  { value: "Europe/London",     label: "London (GMT/BST)" },
  { value: "America/New_York",  label: "New York (ET)" },
  { value: "America/Toronto",   label: "Toronto (ET)" },
  { value: "America/Vancouver", label: "Vancouver (PT)" },
  { value: "Asia/Singapore",    label: "Singapore (SGT)" },
  { value: "Asia/Tokyo",        label: "Tokyo (JST)" },
  { value: "UTC",               label: "UTC" },
];
function localToISOWithTZ(date: string, time: string, tz: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{2}:\d{2}$/.test(time)) return null;
  if (!tz) {
    const d = new Date(`${date}T${time}:00`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  try {
    const [y, m, day] = date.split("-").map(Number);
    const [h, min]    = time.split(":").map(Number);
    const utcGuess = Date.UTC(y, m - 1, day, h, min);
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
    const parts = Object.fromEntries(
      fmt.formatToParts(new Date(utcGuess)).filter((p) => p.type !== "literal")
        .map((p) => [p.type, p.value])
    ) as Record<string, string>;
    const tzUTC = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) % 24, Number(parts.minute), Number(parts.second ?? "0"),
    );
    const offsetMs = tzUTC - utcGuess;
    return new Date(utcGuess - offsetMs).toISOString();
  } catch {
    return null;
  }
}

// Same base URL convention as src/components/hook/bot-form-req.tsx — points
// at the journey_builder backend in prod, can be overridden via the standard
// REACT_APP_BACKEND_URL env var for staging/dev.
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://journeybuilder.connect.dalmlabs.com";

// Auth service (CRM) exposes GET /auth/get_userdata → { payload: { org_id, user_id } }.
// org_id is what the journey_builder backend calls tenant_id. Same URL
// convention as src/components/hook/use-req.tsx.
const AUTH_URL =
  process.env.REACT_APP_AUTH_URL || "https://api.connect.dalmlabs.com";

// localStorage key already used elsewhere (customize-flow-modal.tsx) so we
// share the cache and avoid double fetches.
const TENANT_ID_LS_KEY = "linkedin_tenant_id";

// Backend exposes the canonical list at GET /v1/events/types.
const FALLBACK_EVENT_TYPES: EventTypeOption[] = [
  { label: "Appointment_Scheduled",      value: "Appointment_Scheduled" },
  { label: "Feedback_Collection_Start",  value: "Feedback_Collection_Start" },
  { label: "On_Ticket_Closed",           value: "On_Ticket_Closed" },
  { label: "Ticket_Closed",              value: "Ticket_Closed" },
  { label: "Agent_Assigned",             value: "Agent_Assigned" },
  { label: "Tag_Added",                  value: "Tag_Added" },
  { label: "Reminder",                   value: "Reminder" },
];

interface EventTypeOption {
  label: string;
  value: string;
  description?: string;
}

interface BackendEventType {
  name: string;
  description?: string;
  is_system?: boolean;
  is_active?: boolean;
}

interface OnEventTriggerCardProps {
  node: TriggerNode;
  onChange: (n: TriggerNode) => void;
  onDelete: () => void;
  onReset: () => void;
}

// ── Multi-event support (Joseph 06-30 screenshot) ────────────────────
// The trigger card shows ONE Event dropdown per registered event type.
// Operator clicks "Add an Event" to add another row. Each event is
// stored in node.eventTypes (array). Backwards-compatible with the
// older single-value node.eventType.
function readInitialEventTypes(node: TriggerNode): string[] {
  const raw = (node as any).eventTypes;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.filter((v) => typeof v === "string");
  }
  const single = (node as any).eventType as string | undefined;
  return single ? [single] : [""];
}

export const OnEventTriggerCard: React.FC<OnEventTriggerCardProps> = ({
  node,
  onChange,
  onDelete,
  onReset,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventTypeOption[]>(FALLBACK_EVENT_TYPES);
  // Lead form (only mounts when at least one selected event is an
  // appointment-style event — hidden for Feedback Collection per
  // Joseph 06-30: "i said remove it for only feedback only").
  const initialLeads: TestLead[] = (() => {
    const stored = (node as any).testLeads;
    if (Array.isArray(stored) && stored.length > 0) {
      return stored.map((l: any) => ({
        first_name: typeof l?.first_name === "string" ? l.first_name : "",
        last_name: typeof l?.last_name === "string" ? l.last_name : "",
        phone: typeof l?.phone === "string" ? l.phone : "",
        appointment_date: typeof l?.appointment_date === "string" ? l.appointment_date : "",
        appointment_time: typeof l?.appointment_time === "string" ? l.appointment_time : "",
        timezone: typeof l?.timezone === "string" ? l.timezone : "",
      }));
    }
    return [{ ...EMPTY_LEAD }];
  })();
  const [leads, setLeads] = useState<TestLead[]>(initialLeads);
  const [leadErrs, setLeadErrs] = useState<Record<number, Partial<Record<keyof TestLead, string>>>>({});
  const [, setFiring] = useState(false);

  // Use the same working hook the rest of the app uses (bot-req.tsx)
  // for the actual event POST. Raw fetch was throwing "Failed to fetch"
  // due to inconsistent CORS treatment; useJouneyReq is the pattern
  // publishJourney / createJourney already use successfully.
  const bearerToken = localStorage.getItem("authToken") ?? "";
  const { makeRequest: postEvent } = useJouneyReq(
    "/v1/events/external",
    "POST",
    { Authorization: `Bearer ${bearerToken}` },
  );
  const [loading, setLoading] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState<string[]>(readInitialEventTypes(node));

  const update = (patch: Partial<TriggerNode>) => onChange({ ...node, ...patch });
  const restartJourney = node.restart_journey ?? true;

  const persistEvents = (next: string[]) => {
    // Keep both shapes set so older code reading eventType still works.
    update({
      eventTypes: next as any,
      eventType: (next.find((v) => v) ?? "") as any,
    } as Partial<TriggerNode>);
  };

  const setEventAt = (idx: number, value: string) => {
    setSelectedEvents((curr) => {
      const next = curr.map((v, i) => (i === idx ? value : v));
      persistEvents(next);
      return next;
    });
  };

  const removeEvent = (idx: number) => {
    setSelectedEvents((curr) => {
      const next = curr.length === 1 ? [""] : curr.filter((_, i) => i !== idx);
      persistEvents(next);
      return next;
    });
  };

  // Lead form is mounted only for appointment-style triggers.
  const showLeadForm = selectedEvents.some((e) => APPT_EVENT_TYPES.has(e));
  const showFeedbackForm = !showLeadForm && selectedEvents.some((e) => FB_EVENT_TYPES.has(e));

  // Feedback Collection test dropdown state — collapsible, default
  // closed. Just phone + first name (no date/time — feedback has no
  // appointment). Persists to node.testLeads with an empty date/time
  // so Publish-time fire has no wait_till to compute.
  const [fbOpen, setFbOpen] = useState(false);
  const [fbLead, setFbLead] = useState<{ phone: string; first_name: string }>(() => {
    const stored = (node as any).testLeads;
    if (Array.isArray(stored) && stored[0]) {
      return {
        phone: typeof stored[0].phone === "string" ? stored[0].phone : "",
        first_name: typeof stored[0].first_name === "string" ? stored[0].first_name : "",
      };
    }
    return { phone: "", first_name: "" };
  });
  const [fbErr, setFbErr] = useState<{ phone?: string; first_name?: string }>({});

  const saveFbLead = () => {
    const errs: { phone?: string; first_name?: string } = {};
    if (!fbLead.first_name.trim()) errs.first_name = "Required";
    if (!fbLead.phone.trim()) errs.phone = "Required";
    else if (!/^\+[1-9]\d{6,14}$/.test(fbLead.phone.trim())) errs.phone = "E.164 (+countrycode)";
    if (Object.keys(errs).length) {
      setFbErr(errs);
      toast.error("Fix the highlighted fields");
      return;
    }
    // Persist as a single lead on node.testLeads so Publish auto-fires
    // one event to this phone. Empty date/time = no wait_till compute.
    onChange({
      ...node,
      testLeads: [{
        phone: fbLead.phone.trim(),
        first_name: fbLead.first_name.trim(),
        last_name: "",
        appointment_date: "",
        appointment_time: "",
        timezone: "",
      }],
    } as TriggerNode);
    setFbErr({});
    toast.success("Test contact saved — click Publish to fire the bot");
  };

  const persistLeads = (next: TestLead[]) => {
    onChange({ ...node, testLeads: next } as TriggerNode);
  };
  const setLeadField = (idx: number, field: keyof TestLead, value: string) => {
    setLeads((curr) => {
      const next = curr.map((l, i) => (i === idx ? { ...l, [field]: value } : l));
      persistLeads(next);
      return next;
    });
    setLeadErrs((curr) => {
      if (!curr[idx]?.[field]) return curr;
      const next = { ...curr, [idx]: { ...curr[idx] } };
      delete next[idx][field];
      return next;
    });
  };
  const addLead = () => {
    if (leads.length >= 10) { toast.error("Maximum 10 leads"); return; }
    setLeads((curr) => {
      const next = [...curr, { ...EMPTY_LEAD }];
      persistLeads(next);
      return next;
    });
  };
  const removeLead = (idx: number) => {
    setLeads((curr) => {
      const next = curr.length === 1 ? [{ ...EMPTY_LEAD }] : curr.filter((_, i) => i !== idx);
      persistLeads(next);
      return next;
    });
    setLeadErrs((curr) => {
      const next = { ...curr };
      delete next[idx];
      return next;
    });
  };

  // Submit button: validates + saves the leads onto the trigger node.
  // Joseph 06-30 spec: "when i submit save then i pick the template
  // and other thing then it waits until i create and publish bot".
  // The actual event fires happen in create-builder/index.tsx's Publish
  // handler, which reads trigger.testLeads back off the node.
  const saveLeads = () => {
    const errs: Record<number, Partial<Record<keyof TestLead, string>>> = {};
    leads.forEach((l, i) => {
      const fe: Partial<Record<keyof TestLead, string>> = {};
      if (!l.first_name.trim()) fe.first_name = "Required";
      if (!l.phone.trim()) fe.phone = "Required";
      else if (!/^\+[1-9]\d{6,14}$/.test(l.phone.trim())) fe.phone = "E.164 (+countrycode)";
      if (!l.appointment_date) fe.appointment_date = "Required";
      if (!l.appointment_time) fe.appointment_time = "Required";
      if (Object.keys(fe).length) errs[i] = fe;
    });
    if (Object.keys(errs).length) {
      setLeadErrs(errs);
      toast.error("Fix the highlighted fields");
      return;
    }
    // persistLeads has already been called on every keystroke via
    // setLeadField / addLead / removeLead, so the node already holds
    // the latest values — this final call is a safety net.
    persistLeads(leads);
    setLeadErrs({});
    toast.success(
      `${leads.length} lead${leads.length === 1 ? "" : "s"} saved — configure the rest of the flow, then click Publish`
    );
  };

  // Send Test Event — fires ONE /v1/events/external POST per saved
  // lead so the operator can trigger their published bot end-to-end
  // from the trigger card. Uses the same useJouneyReq hook every other
  // working call in the app uses (avoids the CORS "Failed to fetch"
  // that raw fetch was hitting). Joseph 06-30: replaces the auto-fire
  // that used to happen inside Publish (which caused duplicates + CORS
  // errors).
  const sendTestEvent = async () => {
    // Validate first — same rules as save.
    const errs: Record<number, Partial<Record<keyof TestLead, string>>> = {};
    leads.forEach((l, i) => {
      const fe: Partial<Record<keyof TestLead, string>> = {};
      if (!l.first_name.trim()) fe.first_name = "Required";
      if (!l.phone.trim()) fe.phone = "Required";
      else if (!/^\+[1-9]\d{6,14}$/.test(l.phone.trim())) fe.phone = "E.164 (+countrycode)";
      if (!l.appointment_date) fe.appointment_date = "Required";
      if (!l.appointment_time) fe.appointment_time = "Required";
      if (Object.keys(fe).length) errs[i] = fe;
    });
    if (Object.keys(errs).length) {
      setLeadErrs(errs);
      toast.error("Fix the highlighted fields");
      return;
    }
    if (!bearerToken) { toast.error("Not logged in"); return; }

    // Resolve tenant_id: JWT → cache → /auth/get_userdata.
    let tenantId = "";
    try {
      const decoded: any = jwtDecode(bearerToken);
      tenantId = decoded?.tenant_id ?? decoded?.tenantId ?? decoded?.tenant ?? decoded?.org_id ?? "";
    } catch { /* ignore */ }
    if (!tenantId) tenantId = localStorage.getItem("linkedin_tenant_id") ?? "";
    if (!tenantId) {
      try {
        const authUrl = process.env.REACT_APP_AUTH_URL || "https://api.connect.dalmlabs.com";
        const r = await fetch(`${authUrl}/auth/get_userdata`, {
          headers: { Authorization: `Bearer ${bearerToken}` },
        });
        if (r.ok) {
          const d = await r.json();
          tenantId = d?.payload?.org_id ?? d?.payload?.tenant_id ?? d?.org_id ?? d?.tenant_id ?? "";
          if (tenantId) localStorage.setItem("linkedin_tenant_id", tenantId);
        }
      } catch { /* ignore */ }
    }
    if (!tenantId) { toast.error("Could not resolve tenant_id"); return; }

    setFiring(true);
    const eventType = selectedEvents.find((e) => APPT_EVENT_TYPES.has(e)) || "Appointment_Scheduled";
    const now = new Date().toISOString();
    let ok = 0, fail = 0;
    let firstErr: string | null = null;
    for (const l of leads) {
      const apptISO = localToISOWithTZ(l.appointment_date, l.appointment_time, l.timezone);
      if (!apptISO) { fail++; firstErr = firstErr ?? "Could not parse date/time"; continue; }
      const readable = new Date(apptISO).toLocaleString(undefined, {
        timeZone: l.timezone || undefined,
        day: "2-digit", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
      const body = {
        tenant_id: tenantId,
        event_type: eventType,
        external_id: `test_${l.first_name.toLowerCase()}_${Date.now()}_${ok + fail}`,
        occurred_at: now,
        contact: {
          phone: l.phone.trim(),
          first_name: l.first_name.trim(),
          last_name: l.last_name.trim() || undefined,
        },
        attributes: {
          appointment_date: apptISO,
          scheduled_date_and_time: readable,
          timezone: l.timezone || undefined,
        },
      };
      const [resp, statusCode] = await postEvent(body);
      if (statusCode >= 200 && statusCode < 300) {
        ok++;
      } else {
        fail++;
        firstErr = firstErr ?? `HTTP ${statusCode} — ${resp?.detail ?? resp?.message ?? "unknown"}`;
        // eslint-disable-next-line no-console
        console.error(`[Trigger] postEvent HTTP ${statusCode}`, resp, "body=", body);
      }
    }
    setFiring(false);
    if (fail === 0) toast.success(`Sent ${ok} event${ok === 1 ? "" : "s"} — check WhatsApp`);
    else if (ok === 0) toast.error(`All ${fail} sends failed: ${firstErr}`);
    else toast.error(`${ok} sent, ${fail} failed: ${firstErr}`);
  };

  // Fetch event types from backend once on mount. Tenant scope is enforced
  // server-side via the Bearer token; nothing here is hardcoded except the
  // safety-net fallback that fires only when the endpoint is unreachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${BACKEND_URL}/v1/events/types`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: BackendEventType[] = await res.json();
        if (cancelled) return;
        const next = (data ?? [])
          .filter((t) => t.is_active !== false)
          .map((t) => ({
            label: t.name,
            value: t.name,
            description: t.description ?? "",
          }));
        if (next.length > 0) setEventTypes(next);
      } catch (err) {
        // Backend endpoint not deployed yet OR network error — keep
        // showing the fallback list so journey design isn't blocked.
        // eslint-disable-next-line no-console
        console.warn("[OnEvent] Could not fetch /v1/events/types — using fallback list", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <div className="relative">
        <div className="absolute -top-7 left-0">
          <span className="bg-gray-800 text-white text-xs px-3 py-1 rounded-full">
            Bot Starting Point
          </span>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-[300px] relative">
          <RightDot />
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-indigo-50 rounded px-1.5 py-0.5">
              Trigger
            </span>
            <NodeMenu onDelete={() => setShowDeleteModal(true)} onReset={onReset} />
          </div>

          <p className="text-indigo-600 font-semibold text-sm mb-0.5">On Event</p>
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-4">
            Fires when a specific event occurs
          </p>

          {/* One Event dropdown per registered event type. (Joseph
              06-30 screenshot shows two stacked boxes — operator can
              register the same trigger card for multiple events.) */}
          {selectedEvents.map((current, idx) => (
            <div key={idx} className="rounded-lg bg-indigo-50/80 p-2.5 mb-3 relative">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Event {loading && idx === 0 && (
                    <span className="text-[#7B7B7B] font-semibold dark:text-gray-500 font-medium">(loading…)</span>
                  )}
                </label>
                {selectedEvents.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEvent(idx)}
                    className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
                    title="Remove event"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="relative">
                <select
                  value={current}
                  onChange={(e) => setEventAt(idx, e.target.value)}
                  className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-indigo-600 appearance-none bg-white dark:bg-gray-900 pr-9 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                >
                  <option value="">— pick an event —</option>
                  {eventTypes.map((option) => (
                    <option key={option.value} value={option.value} title={option.description}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-600 pointer-events-none">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <circle cx="8" cy="12" r="2" />
                    <path d="M13 10h5M13 14h4" />
                  </svg>
                </div>
              </div>
            </div>
          ))}

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={restartJourney}
              onChange={(e) => update({ restart_journey: e.target.checked })}
              className="mt-0.5 w-4 h-4 rounded accent-indigo-600 flex-shrink-0"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              When this triggers, restart the user journey.
            </span>
          </label>

          {/* Test lead form — only mounts when the trigger is appointment-
              style. Hidden for Feedback_Collection_Start / On_Ticket_Closed
              (Joseph 06-30: "i said remove it for only feedback only"). */}
          {showLeadForm && (
            <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="space-y-3">
                {leads.map((lead, idx) => {
                  const e = leadErrs[idx] ?? {};
                  const inputCls = (bad?: string) =>
                    `w-full border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 ${
                      bad
                        ? "border-red-400 ring-red-200 bg-red-50/40"
                        : "border-gray-200 dark:border-gray-700 focus:ring-indigo-300 bg-white dark:bg-gray-900"
                    }`;
                  return (
                    <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900 relative">
                      {leads.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLead(idx)}
                          className="absolute top-2 right-2 text-gray-300 dark:text-gray-600 hover:text-red-500"
                          title="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}

                      <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        placeholder="+234 123 456 7890"
                        value={lead.phone}
                        onChange={(ev) => setLeadField(idx, "phone", ev.target.value)}
                        className={inputCls(e.phone)}
                      />
                      {e.phone && <p className="text-[10px] text-red-500 mt-0.5">{e.phone}</p>}

                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">First Name</label>
                          <input
                            type="text"
                            placeholder="John"
                            value={lead.first_name}
                            onChange={(ev) => setLeadField(idx, "first_name", ev.target.value)}
                            className={inputCls(e.first_name)}
                          />
                          {e.first_name && <p className="text-[10px] text-red-500 mt-0.5">{e.first_name}</p>}
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">Last Name</label>
                          <input
                            type="text"
                            placeholder="Doe"
                            value={lead.last_name}
                            onChange={(ev) => setLeadField(idx, "last_name", ev.target.value)}
                            className={inputCls(e.last_name)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">Date</label>
                          <input
                            type="date"
                            value={lead.appointment_date}
                            onChange={(ev) => setLeadField(idx, "appointment_date", ev.target.value)}
                            className={inputCls(e.appointment_date)}
                          />
                          {e.appointment_date && <p className="text-[10px] text-red-500 mt-0.5">{e.appointment_date}</p>}
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">Time</label>
                          <input
                            type="time"
                            value={lead.appointment_time}
                            onChange={(ev) => setLeadField(idx, "appointment_time", ev.target.value)}
                            className={inputCls(e.appointment_time)}
                          />
                          {e.appointment_time && <p className="text-[10px] text-red-500 mt-0.5">{e.appointment_time}</p>}
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">Timezone</label>
                        <select
                          value={lead.timezone}
                          onChange={(ev) => setLeadField(idx, "timezone", ev.target.value)}
                          className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                        >
                          {TIMEZONE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addLead}
                disabled={leads.length >= 10}
                className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] text-indigo-500 hover:text-indigo-700 disabled:text-gray-300 border border-dashed border-indigo-300 rounded-lg py-2 hover:border-indigo-400 transition-colors"
              >
                <Plus size={12} />
                Add another {leads.length >= 10 && "(max 10)"}
              </button>

              <button
                type="button"
                onClick={saveLeads}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              >
                <Play size={12} />
                Submit
              </button>

              <p className="mt-1.5 text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 text-center">
                Configure the rest of the flow, then click Publish to fire.
              </p>
            </div>
          )}

          {/* Feedback Collection test dropdown — collapsible, default
              closed. Just phone + first name inputs. Save writes to
              node.testLeads; Publish handler auto-fires one event.
              (Joseph 07-01: "add a test to the feedback collection
              card like the event ... but it will be a dropdown ...
              where I can input a phone number to collect feedback"). */}
          {showFeedbackForm && (
            <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
              <button
                type="button"
                onClick={() => setFbOpen((o) => !o)}
                className="w-full flex items-center justify-between text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:text-indigo-600"
              >
                <span>Send this bot to a test phone</span>
                <span className="text-[14px] text-[#7B7B7B] font-semibold dark:text-gray-500">{fbOpen ? "−" : "+"}</span>
              </button>

              {fbOpen && (
                <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
                  <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="+234 123 456 7890"
                    value={fbLead.phone}
                    onChange={(e) => {
                      setFbLead((c) => ({ ...c, phone: e.target.value }));
                      setFbErr((c) => ({ ...c, phone: undefined }));
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 ${
                      fbErr.phone
                        ? "border-red-400 ring-red-200 bg-red-50/40"
                        : "border-gray-200 dark:border-gray-700 focus:ring-indigo-300 bg-white dark:bg-gray-900"
                    }`}
                  />
                  {fbErr.phone && (
                    <p className="text-[10px] text-red-500 mt-0.5">{fbErr.phone}</p>
                  )}

                  <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1 mt-3">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="John"
                    value={fbLead.first_name}
                    onChange={(e) => {
                      setFbLead((c) => ({ ...c, first_name: e.target.value }));
                      setFbErr((c) => ({ ...c, first_name: undefined }));
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 ${
                      fbErr.first_name
                        ? "border-red-400 ring-red-200 bg-red-50/40"
                        : "border-gray-200 dark:border-gray-700 focus:ring-indigo-300 bg-white dark:bg-gray-900"
                    }`}
                  />
                  {fbErr.first_name && (
                    <p className="text-[10px] text-red-500 mt-0.5">{fbErr.first_name}</p>
                  )}

                  <button
                    type="button"
                    onClick={saveFbLead}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  >
                    <Play size={12} />
                    Submit
                  </button>
                  <p className="mt-1.5 text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 text-center">
                    Click Publish to fire the bot to this phone.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          title="Delete trigger?"
          description="This will remove the trigger and any connected flow from your bot journey. This action cannot be undone."
          confirmLabel="Delete Trigger"
          onConfirm={() => { onDelete(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};